import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import {
  bootstrapActivityIfMissing,
  clearLastActivity,
  isSessionExpired,
  loadSessionTimeoutConfig,
  recordActivity,
} from '../lib/sessionTimeout';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const bootstrapSession = async () => {
      await loadSessionTimeoutConfig(API_URL);

      const token = localStorage.getItem("auth_token");
      const savedUser = localStorage.getItem("user");
      if (token && savedUser) {
        if (isSessionExpired()) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("user");
          clearLastActivity();
          if (!cancelled) setLoading(false);
          return;
        }

        bootstrapActivityIfMissing();

        try {
          if (!cancelled) setUser(JSON.parse(savedUser));
        } catch {
          localStorage.removeItem("user");
        }
        axios
          .get(`${API_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
          .then((response) => {
            if (cancelled) return;
            localStorage.setItem("user", JSON.stringify(response.data));
            setUser(response.data);
          })
          .catch((error) => {
            if (cancelled) return;
            const detail = error.response?.data?.detail;
            if (
              error.response?.status === 401 &&
              typeof detail === "string" &&
              detail.toLowerCase().includes("inactivity")
            ) {
              localStorage.removeItem("auth_token");
              localStorage.removeItem("user");
              clearLastActivity();
              setUser(null);
            }
          });
      }

      if (!cancelled) setLoading(false);
    };

    bootstrapSession();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = (token, userData) => {
    localStorage.setItem("auth_token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    recordActivity();
    setUser(userData);
    return userData;
  };

  const refreshUser = async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) return null;
    const response = await axios.get(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    localStorage.setItem("user", JSON.stringify(response.data));
    setUser(response.data);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user");
    clearLastActivity();
    setUser(null);
  };

  const updateUser = (userData) => {
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, refreshUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
